import LinkifyIt from 'linkify-it';
import type { DebounceSettings, DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';
import escape from 'lodash/escape';
import isObject from 'lodash/isObject';
import memoize from 'lodash/memoize';

const linkifyInstance = new LinkifyIt();

export function linkify(text: string, _options: { target?: string; rel?: string } = {}) {
  try {
    const matches = linkifyInstance.match(text);
    if (!matches) return text;

    let result = text;
    // Process in reverse to avoid index shifting
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      // Escape both the URL and the visible text before interpolating into HTML.
      // linkify-it includes characters like `"` in the matched URL, which would
      // otherwise break out of the href attribute and inject event handlers (XSS),
      // since the result is rendered via dangerouslySetInnerHTML downstream.
      const link = `<a href="${escape(match.url)}" target="_blank" rel="noopener noreferrer">${escape(match.text)}</a>`;
      result = result.slice(0, match.index) + link + result.slice(match.lastIndex);
    }

    return result;
  } catch (_e) {
    console.error('Failed to linkify text', text, _e);

    return text;
  }
}

/**
 * Returns a normalized location path (without filename)
 * e.g. /index-app.html -> /
 */
export function getNormalizedLocationPath(location: Location) {
  let pathname = location.pathname;
  const unnecessaryLocationPath = location.pathname.match(/\/([a-zA-Z\-_]*\.html?)/);

  if (unnecessaryLocationPath) {
    pathname = pathname.replace(unnecessaryLocationPath[1], '');
  }

  return pathname;
}

export function prettyNumber(number: number, precision = 2) {
  return Math.round(number * (10 ** precision)) / (10 ** precision);
}

/**
 * Combines array.
 * Useful to reduce multiple arrays to one.
 *
 * `[[]].reduce(combineArray, []);`
 */
export const combineArray = <T1, T2 = T1>(arr1: T1[], arr2: T2[]) => {
  return [...arr1, ...arr2];
};

/**
 * Creates human-readable file sizes.
 */
export function prettyFileSize(fileSize: number) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let size = fileSize;
  let index = 0;

  while (size >= 1024) {
    size /= 1024;
    index++;
  }

  return size === Math.floor(size)
    ? `${size} ${units[index]}`
    : `${size.toFixed(1)} ${units[index]}`;
}

/**
 * Returns tuple of count of elements that passes and not passes the check
 * respectively.
 */
export const passes = <R = any>(arr: R[], check: (v: R) => boolean = Boolean) => {
  return arr.reduce(
    ([t, f], v) => check(v) ? [t + 1, f] : [t, f + 1],
    [0, 0]
  ) as [number, number];
};

/**
 * Replaces properties of an interface.
 * usage: type X = Override<InputProps, { value: MyInputType }>;
 * https://github.com/Microsoft/TypeScript/issues/12215#issuecomment-393936055
 */
export type Overwrite<T1, T2> = Pick<T1, Exclude<keyof T1, keyof T2>> & T2;

/**
 * transforms user answer input (may be expanded in future)
 *
 * - have „“ be auto transformed into ""
 * - have ’‘ be transformed into '
 */
export const normalizeTextInputAnswer = (answer = '') =>
  answer
    .replace(/[“”„]/g, '"')
    .replace(/[’‘]/g, '\'');

/**
 * normalize and compare user answer input
 *
 * @param given users answer
 * @param expected expected answer
 */
export const isUserAnswerInputCorrect = (given: string, expected: string) =>
  normalizeTextInputAnswer(given).trim() === normalizeTextInputAnswer(expected).trim();

/**
 * Delay returning the given functions result.
 *
 * Useful if you want to test/debug behavior with different timings.
 */
export const makeSlow = (cb: (...args: any[]) => any, delay = 5000) => {
  return async (...args: any[]) => {
    await new Promise(resolve => setTimeout(resolve, delay));

    return cb(...args);
  };
};

/**
 * Splits the given string into multiple strings with a maximum length of `maxChars`.
 * Preserves sentence structure (chunks will be created with complete sentences and not cut them off).
 */
export const splitSentences = (str_: string, chunkSize = 12000) => {
  // replace multiple spaces with single space
  const str = str_.replace(/\s+/g, ' ');
  const arr = str.split(/([.?!])/);
  const newArr: string[] = [];
  for (let i = 0; i < arr.length; i++) {
    let temp = '';
    for (let j = i; j < arr.length; j++) {
      if (temp.length + arr[j].length < chunkSize) {
        temp += arr[j];
        i = j;
      } else {
        break;
      }
    }
    newArr.push(temp);
  }
  return newArr;
};

/**
 * Memoized debounce function via https://github.com/lodash/lodash/issues/2403#issuecomment-1158234729
 *
 * This can be used to create debounced functions which are memoized by their arguments.
 */
interface MemoizeDebouncedFunction<F extends (...args: any[]) => any> {
  (...args: Parameters<F>): void;
  flush: (...args: Parameters<F>) => void;
}

export function memoizeDebounce<F extends (...args: any[]) => any>(
  func: F,
  wait = 0,
  options: DebounceSettings & {
    // when specified, memoized functions will be flushed after this time
    // useful when you want to automatically remove no longer needed functions
    cacheTTL?: number;
  } = {},
  resolver?: (...args: Parameters<F>) => unknown
): MemoizeDebouncedFunction<F> {
  const debounceMemo = memoize<(...args: Parameters<F>) => DebouncedFunc<F>>(
    (..._args: Parameters<F>) => {
      if (typeof options.cacheTTL === 'number') {
        setTimeout(() => {
          debounceMemo.cache.delete(resolver ? resolver(..._args) : _args[0]);
        }, options.cacheTTL);
      }

      return debounce(func, wait, options);
    },
    resolver // eslint-disable-line @stylistic/indent
  );

  function wrappedFunction(...args: Parameters<F>): ReturnType<F> | undefined {
    return debounceMemo(...args)(...args);
  }

  wrappedFunction.flush = (...args: Parameters<F>): void => {
    debounceMemo(...args).flush();
  };

  return wrappedFunction as unknown as MemoizeDebouncedFunction<F>;
}

export const pickRecursive = (
  currentValues: any,
  toPick: string[],
) => {
  const newObj: DeepPartial<typeof currentValues> = {};

  // Group paths by their top-level keys
  const pathsByTopKey: Record<string, any[]> = {};

  toPick.forEach(path => {
    const pathParts = path.split('.');
    const topKey = pathParts[0];
    const restPath = pathParts.slice(1).join('.');

    if (!pathsByTopKey[topKey]) {
      pathsByTopKey[topKey] = [];
    }

    if (restPath) {
      pathsByTopKey[topKey].push(restPath);
    } else {
      pathsByTopKey[topKey].push(null); // Indicates a top-level field
    }
  });

  // Process each top-level key
  Object.keys(pathsByTopKey).forEach(topKey => {
    const subPaths = pathsByTopKey[topKey];
    const value = currentValues[topKey];

    if (subPaths.includes(null)) {
      // Top-level field, directly assign the value
      if (value !== undefined) {
        newObj[topKey] = value;
      }
    } else if (Array.isArray(value)) {
      // Handle arrays of objects
      const fieldKeys = subPaths;
      newObj[topKey] = value.map(item => pickRecursive(item, fieldKeys));
    } else if (isObject(value)) {
      // Handle nested objects
      const fieldKeys = subPaths;
      const newValue = pickRecursive(value, fieldKeys);
      if (newValue !== null && newValue !== undefined) {
        newObj[topKey] = newValue;
      }
    }
  });

  return Object.keys(newObj).length ? newObj : null;
};

export const omitRecursive = (
  currentValues: any,
  toOmit: string[],
): DeepPartial<typeof currentValues> => {
  const newObj: DeepPartial<typeof currentValues> = {};

  // Group paths by their top-level keys
  const pathsByTopKey: Record<string, any[]> = {};

  toOmit.forEach(path => {
    const pathParts = path.split('.');
    const topKey = pathParts[0];
    const restPath = pathParts.slice(1).join('.');

    if (!pathsByTopKey[topKey]) {
      pathsByTopKey[topKey] = [];
    }

    if (restPath) {
      pathsByTopKey[topKey].push(restPath);
    } else {
      pathsByTopKey[topKey].push(null); // Indicates the entire top-level field
    }
  });

  // Iterate over all keys in the original object
  Object.keys(currentValues).forEach(topKey => {
    const value = currentValues[topKey];
    const subPaths = pathsByTopKey[topKey];

    if (subPaths && subPaths.includes(null)) {
      // Entire top-level key is to be omitted; skip it
      return;
    }

    if (subPaths) {
      // There are specific sub-paths to omit within this top-level key
      if (Array.isArray(value)) {
        // Handle arrays of objects
        newObj[topKey] = value.map(item => omitRecursive(item, subPaths.filter(p => p)));
      } else if (isObject(value)) {
        // Handle nested objects
        newObj[topKey] = omitRecursive(value, subPaths.filter(p => p));
      } else {
        // If it's neither an array nor an object, and not fully omitted, retain the value
        newObj[topKey] = value;
      }
    } else {
      // No keys to omit for this top-level key; retain it as is
      newObj[topKey] = value;
    }
  });

  return newObj;
};

/**
 * Wrap any function to log its execution time
 */
export const time = async <Fn extends (...args: any) => any>(
  name: string,
  fn: Fn
): Promise<ReturnType<Fn>> => {
  const now = performance.now();

  const result = await fn();

  // eslint-disable-next-line no-console
  console.log(`${name} Time: ${performance.now() - now}ms`);

  return result;
};

export const isInputFocused = () => {
  const activeElement = document.activeElement;
  const isInputField = activeElement?.tagName === 'TEXTAREA' || activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'IFRAME';

  return isInputField;
};
